import { useMemo, useState } from 'react';
import { AlertTriangle, Pencil, Search, Wine, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils/cn';
import { cantidadDe, formatoCantidad } from '@/lib/utils/formato-precio';
import type { ProductoDeComercia } from '../../types/comercia.types';
import { kg, m3, medidas, siglaDeUnidad, usd, usdExacto } from '../../utils/formatos';

/**
 * La lista de lo que se trae (maqueta de Melanie, Comercia › Productos): el color
 * con el que se ve en el plano de carga, la partida, la cantidad y lo que mide,
 * pesa y cuesta cada unidad.
 */
export function TablaDeProductos({
  productos,
  resumen,
  onEditar,
  onQuitar,
}: {
  productos: ProductoDeComercia[];
  resumen: { fob: number; volumenM3: number };
  onEditar: (p: ProductoDeComercia) => void;
  onQuitar: (p: ProductoDeComercia) => void;
}) {
  const [filtro, setFiltro] = useState('');
  const visibles = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    if (!q) return productos;
    return productos.filter((p) =>
      [p.descripcion, p.codigoPartida ?? '', p.proveedor ?? ''].some((t) =>
        t.toLowerCase().includes(q)
      )
    );
  }, [productos, filtro]);

  return (
    <section
      aria-label="Productos de la importación"
      className="overflow-hidden rounded-tarjeta border bg-card shadow-sm"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
        <label className="relative w-full max-w-[400px]">
          <span className="sr-only">Buscar producto o partida</span>
          <Search
            className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Buscar producto o partida"
            className="h-11 w-full rounded-xl border border-input bg-surface-sunken pl-11 pr-4 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </label>
        <p className="text-sm text-muted-foreground tabular-nums">
          {cantidadDe(productos.length, 'producto', 'productos')} · {usd(resumen.fob)} FOB ·{' '}
          {m3(resumen.volumenM3)}
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[1040px] text-[15px]">
          <thead className="bg-surface-sunken text-sm text-muted-foreground">
            <tr>
              <th scope="col" className="w-12 px-5 py-3">
                <span className="sr-only">Color</span>
              </th>
              <th scope="col" className="px-3 py-3 text-left font-semibold">
                Producto
              </th>
              <th scope="col" className="px-3 py-3 text-left font-semibold">
                Partida
              </th>
              <th scope="col" className="px-3 py-3 text-right font-semibold">
                Cantidad
              </th>
              <th scope="col" className="px-3 py-3 text-left font-semibold">
                Dimensiones
              </th>
              <th scope="col" className="px-3 py-3 text-right font-semibold">
                Vol. / u.
              </th>
              <th scope="col" className="px-3 py-3 text-right font-semibold">
                Peso / u.
              </th>
              <th scope="col" className="px-3 py-3 text-right font-semibold">
                FOB / u.
              </th>
              <th scope="col" className="w-24 px-5 py-3">
                <span className="sr-only">Acciones</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {visibles.map((p) => (
              <tr key={p.id} className="transition-colors hover:bg-muted/40">
                <td className="px-5 py-4">
                  <span
                    className="block h-3.5 w-3.5 rounded-full"
                    style={{ backgroundColor: p.color }}
                    aria-hidden="true"
                  />
                </td>
                <td className="max-w-[300px] px-3 py-4">
                  <p className="flex items-center gap-1.5 font-semibold">
                    <span className="truncate" title={p.descripcion}>
                      {p.descripcion}
                    </span>
                    {p.fragil && (
                      <span
                        className="shrink-0 rounded-full bg-info-suave px-1.5 py-0.5 text-[11px] font-semibold text-info"
                        title="Frágil: encima sólo va el mismo producto"
                      >
                        <Wine className="inline h-3 w-3" aria-hidden="true" /> Frágil
                      </span>
                    )}
                  </p>
                  {p.proveedor && (
                    <p className="truncate text-xs text-muted-foreground">{p.proveedor}</p>
                  )}
                </td>
                <td className="px-3 py-4">
                  {p.partida ? (
                    <span className="font-mono text-sm" title={p.partida.descripcion}>
                      {p.partida.codigo}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-bloqueo-suave px-2 py-0.5 text-xs font-semibold text-bloqueo">
                      <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                      Falta partida
                    </span>
                  )}
                </td>
                <td className="px-3 py-4 text-right tabular-nums">
                  {formatoCantidad(p.cantidad)} {siglaDeUnidad(p.unidad)}
                </td>
                <td className="px-3 py-4 text-muted-foreground tabular-nums">
                  {p.largoCm && p.anchoCm && p.altoCm ? (
                    medidas(p.largoCm, p.anchoCm, p.altoCm)
                  ) : (
                    <span className="text-bloqueo">sin medidas</span>
                  )}
                </td>
                <td className="px-3 py-4 text-right tabular-nums">
                  {p.volumenUnitario ? m3(p.volumenUnitario, 3) : '—'}
                </td>
                <td className="px-3 py-4 text-right tabular-nums">
                  {p.pesoUnitario != null ? kg(p.pesoUnitario) : '—'}
                </td>
                <td className="px-3 py-4 text-right font-semibold tabular-nums">
                  {usdExacto(p.fobUnitario)}
                </td>
                <td className="px-5 py-4">
                  <div className="flex justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => onEditar(p)}
                      className="grid h-9 w-9 place-items-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      aria-label={`Editar ${p.descripcion}`}
                    >
                      <Pencil className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onQuitar(p)}
                      className={cn(
                        'grid h-9 w-9 place-items-center rounded-full text-destructive hover:bg-bloqueo-suave focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                      )}
                      aria-label={`Quitar ${p.descripcion}`}
                    >
                      <Trash2 className="h-4 w-4" aria-hidden="true" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {visibles.length === 0 && (
              <tr>
                <td colSpan={9} className="px-5 py-8 text-center text-sm text-muted-foreground">
                  Ningún producto coincide con «{filtro.trim()}».
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
